import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// The autonomy ladder (E09-S6-T3), ported from autonomy() in mockups/kit/components/trust.js and
// section 17 of mockups/kit/surface.css: what the assistant may do, as six rungs in a fixed order,
// each with its one-line note, and commit at the foot, locked, 'Always you'.
//
// What is allowed comes from the edge policy and nothing else (rule 1): session.policy.autonomyCap,
// which the shell sets once it has verified the policy event's signature (reduce.ts keeps the JWS;
// E10-S2-T5, E11-S3-T6 verify it). Nothing the assistant says can move a rung. Rungs up to the cap
// are allowed; rungs above it are off for this organisation.
//
// Deny by default (rule 3): before a policy has arrived, only observe is allowed, which is the
// contract's floor (FLOOR_POLICY). A cap that is not one of the five AutonomyCap values is read as
// observe, never as more, and reported through onInvalidCap.
//
// Commit is not a control (rule 10, ADR-015: the policy type has no rung for it). CommitRung takes
// no props, so no cap, handler or state can reach it; it renders no button, no toggle, no tab stop
// and no handler, for every cap. No rung is a control: the cap is the organisation's, set in
// policy, and the user reads it here.
//
// Where this differs from the kit:
//   - The kit's rungs are all on and say 'On'. Here a rung is allowed or off, by the cap, and says
//     'Allowed' or 'Off for this organisation' (the catalogue's words); the off rung's look is new.
import { useEffect, useId, useRef } from "react";
import { useStore } from "zustand";
import { AutonomyCap } from "@waypoint/contracts";
import { t } from "../../i18n/t.js";
import { Icon } from "../../icons/index.js";
import { cn } from "../../ui/cn.js";
import "./autonomy-ladder.css";
/** The six rungs, in the order the ladder shows them. Commit is last and is never a cap. */
export const autonomyRungs = [
    "observe",
    "explain",
    "point",
    "navigate",
    "prepare",
    "commit",
];
/** The cap with no policy, and the cap an invalid one is read as: the contract's floor. */
export const FLOOR_CAP = "observe";
const CAPS = AutonomyCap.options;
function isCap(value) {
    return typeof value === "string" && CAPS.includes(value);
}
/** A received cap as it may be written to a log: an id's shape, or a placeholder. Never free text. */
export function loggableCap(received) {
    return typeof received === "string" && /^[a-z][a-z0-9-]{0,39}$/.test(received)
        ? received
        : "(not an autonomy cap)";
}
/**
 * The cap in force. No policy: observe. A cap that is one of the five: that cap. Anything else,
 * "commit" among it: observe, with what arrived noted for the report.
 */
export function readCap(policy) {
    if (policy == null)
        return { cap: FLOOR_CAP, invalid: null };
    // Read as unknown: the type says AutonomyCap, and this is the check that it is one.
    const received = policy.autonomyCap;
    return isCap(received)
        ? { cap: received, invalid: null }
        : { cap: FLOOR_CAP, invalid: loggableCap(received) };
}
/** A rung's status under a cap. Commit is locked for every cap; a cap that is not one is the floor. */
export function rungStatus(rung, cap) {
    if (rung === "commit")
        return "locked";
    const inForce = isCap(cap) ? cap : FLOOR_CAP;
    return autonomyRungs.indexOf(rung) <= autonomyRungs.indexOf(inForce) ? "allowed" : "off";
}
/** The cap in force for the view: from the verified edge policy, or the floor. */
export function selectAutonomyCap(s) {
    return readCap(s.session.policy).cap;
}
function selectInvalid(s) {
    return readCap(s.session.policy).invalid;
}
function warnInvalid(received) {
    console.warn(`[waypoint] autonomy cap "${received}" is not one of the five; showing observe only.`);
}
const COPY = {
    observe: { label: "autonomy.observe", note: "autonomy.observeNote" },
    explain: { label: "autonomy.explain", note: "autonomy.explainNote" },
    point: { label: "autonomy.point", note: "autonomy.pointNote" },
    navigate: { label: "autonomy.navigate", note: "autonomy.navigateNote" },
    prepare: { label: "autonomy.prepare", note: "autonomy.prepareNote" },
};
const CAP_RUNGS = autonomyRungs.filter((r) => r !== "commit");
/** A rung the cap governs: a line of text, not a control. */
function Rung({ rung, status, compact }) {
    const allowed = status === "allowed";
    return (_jsxs("li", { className: cn("wp-rung", allowed ? "is-on" : "is-off"), "data-rung": rung, "data-status": status, children: [_jsx("span", { className: "wp-rung__node", children: allowed ? _jsx(Icon, { name: "check", size: 12 }) : null }), _jsxs("div", { className: "wp-rung__text", children: [_jsx("span", { className: "wp-rung__label", children: t(COPY[rung].label) }), compact ? null : _jsx("span", { className: "wp-rung__note", children: t(COPY[rung].note) })] }), _jsx("span", { className: "wp-rung__state", children: allowed ? t("autonomy.allowed") : t("autonomy.off") })] }));
}
/**
 * Commit. No props: nothing — no cap, no handler, no state — can reach it, so it reads the same
 * under every policy and there is nothing on it to press.
 */
function CommitRung() {
    return (_jsxs("li", { className: "wp-rung is-locked", "data-rung": "commit", "data-status": "locked", children: [_jsx("span", { className: "wp-rung__node", children: _jsx(Icon, { name: "lock", size: 12 }) }), _jsxs("div", { className: "wp-rung__text", children: [_jsx("span", { className: "wp-rung__label", children: t("autonomy.commit") }), _jsx("span", { className: "wp-rung__note", children: t("autonomy.commitNote") })] }), _jsxs("span", { className: "wp-rung__state wp-rung__state--locked", children: [_jsx(Icon, { name: "lock", size: 12 }), t("autonomy.alwaysYou")] })] }));
}
/** What the assistant may do in this organisation, and the one thing it never does. */
export function AutonomyLadder({ store, compact = false, className, onInvalidCap, }) {
    const titleId = useId();
    const cap = useStore(store, selectAutonomyCap);
    const invalid = useStore(store, selectInvalid);
    const report = useRef(onInvalidCap ?? warnInvalid);
    report.current = onInvalidCap ?? warnInvalid;
    useEffect(() => {
        if (invalid !== null)
            report.current(invalid);
    }, [invalid]);
    return (_jsxs("section", { className: cn("wp-auto", compact && "wp-auto--compact", className), "aria-labelledby": titleId, "data-autonomy": "", "data-cap": cap, children: [_jsx("header", { className: "wp-head", children: _jsxs("div", { className: "wp-head__main", children: [_jsx("span", { className: "wp-head__icon", children: _jsx(Icon, { name: "shield", size: 16 }) }), _jsxs("div", { className: "wp-head__text", children: [_jsx("h3", { id: titleId, className: "wp-head__title", children: t("autonomy.title") }), compact ? null : _jsx("p", { className: "wp-head__sub", children: t("autonomy.subtitle") })] })] }) }), _jsxs("ol", { className: "wp-ladder", children: [CAP_RUNGS.map((rung) => {
                        const status = rungStatus(rung, cap);
                        return (_jsx(Rung, { rung: rung, status: status === "allowed" ? "allowed" : "off", compact: compact }, rung));
                    }), _jsx(CommitRung, {})] })] }));
}
//# sourceMappingURL=autonomy-ladder.js.map